import { useCallback, useEffect, useRef, useState } from 'react';

import { DEFAULT_QUERY, queryParams } from './organisationUserFormat';

const SEARCH_DELAY_MS = 300;
const SAVE_DELAY_MS = 600;

/**
 * The Users list: its query (seeded from the viewer's saved preference), the
 * current page of rows and the meta beside them. Every change to the query is
 * saved back as the preference.
 */
export default function useOrganisationUserList({ listUrl, listSettingsUrl, initialQuery }) {
    const [query, setQueryState] = useState({ ...DEFAULT_QUERY, ...initialQuery });
    const [searchInput, setSearchInput] = useState(initialQuery?.search ?? '');
    const [page, setPage] = useState(1);
    const [rows, setRows] = useState([]);
    const [meta, setMeta] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [reloadKey, setReloadKey] = useState(0);

    const readSeq = useRef(0);
    const firstQuery = useRef(true);

    const setQuery = useCallback((change) => {
        setQueryState((previous) => ({ ...previous, ...change }));
        setPage(1);
    }, []);

    useEffect(() => {
        const timer = setTimeout(() => {
            const search = searchInput.trim() === '' ? null : searchInput.trim();

            if (search !== query.search) {
                setQuery({ search });
            }
        }, SEARCH_DELAY_MS);

        return () => clearTimeout(timer);
    }, [searchInput, query.search, setQuery]);

    useEffect(() => {
        const sequence = ++readSeq.current;

        setLoading(true);
        setError(null);

        axios
            .get(listUrl, { params: { ...queryParams(query), page } })
            .then(({ data }) => {
                // Only the newest read may land, so the table never lags the
                // filters above it.
                if (sequence === readSeq.current) {
                    setRows(data.data);
                    setMeta(data.meta);
                }
            })
            .catch(() => {
                if (sequence === readSeq.current) {
                    setError($t('Something went wrong'));
                }
            })
            .finally(() => {
                if (sequence === readSeq.current) {
                    setLoading(false);
                }
            });
    }, [listUrl, query, page, reloadKey]);

    useEffect(() => {
        if (firstQuery.current) {
            firstQuery.current = false;

            return undefined;
        }

        const timer = setTimeout(() => {
            axios.put(listSettingsUrl, queryParams(query)).catch(() => {});
        }, SAVE_DELAY_MS);

        return () => clearTimeout(timer);
    }, [listSettingsUrl, query]);

    const clear = useCallback(() => {
        setSearchInput('');
        setQueryState((previous) => ({ ...DEFAULT_QUERY, sort: previous.sort, direction: previous.direction, per_page: previous.per_page }));
        setPage(1);
    }, []);

    const refresh = useCallback(() => setReloadKey((key) => key + 1), []);

    return { query, setQuery, searchInput, setSearchInput, page, setPage, rows, meta, loading, error, clear, refresh };
}
